import React from 'react';
import { Link } from 'react-router-dom';
import {
  Building,
  CheckCircle,
  Clock,
  Users,
  FileText,
  DollarSign,
  BarChart3,
  Shield,
  ArrowRight,
  Calendar,
  MapPin,
  Phone,
  Settings,
  Wrench,
  AlertTriangle
} from 'lucide-react';

const FacilityMaintenanceIndustryPage: React.FC = () => {
  return (
    <div className="bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-gray-800 dark:to-gray-900">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/10 to-indigo-800/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-4">
                <Building className="h-8 w-8 text-indigo-600 mr-3" />
                <span className="text-sm font-semibold text-indigo-600 uppercase tracking-wider">Facility Maintenance</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                Streamline Your Facility Maintenance Operations
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
                Keep commercial buildings running smoothly with preventive maintenance and rapid response repairs. 
                From office buildings to retail spaces, maintain your facilities efficiently.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/signup?industry=0"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-all transform hover:scale-105 shadow-lg"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-indigo-600 bg-white dark:bg-gray-800 border-2 border-indigo-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                >
                  View Pricing
                </Link>
              </div>
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                14-day free trial • No credit card required • Cancel anytime
              </p>
            </div>
            <div className="relative">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8">
                <div className="space-y-6">
                  <div className="flex items-center">
                    <Wrench className="h-8 w-8 text-indigo-600 mr-4" />
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">Preventive Maintenance</p>
                      <p className="text-gray-600 dark:text-gray-400">Scheduled maintenance programs</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <AlertTriangle className="h-8 w-8 text-indigo-500 mr-4" />
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">Emergency Response</p>
                      <p className="text-gray-600 dark:text-gray-400">Rapid repair services</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Settings className="h-8 w-8 text-indigo-700 mr-4" />
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">System Management</p>
                      <p className="text-gray-600 dark:text-gray-400">HVAC, electrical & plumbing</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Facility Maintenance-Specific Features */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Built for Facility Managers
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Everything you need to manage your facility maintenance efficiently
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-indigo-100 dark:bg-indigo-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Calendar className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Preventive Maintenance Scheduling
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Schedule regular maintenance for HVAC, electrical, and plumbing systems to prevent costly breakdowns.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-blue-100 dark:bg-blue-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <MapPin className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Multi-Building Management
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Manage multiple buildings and facilities from a centralized dashboard with location-specific tracking.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-green-100 dark:bg-green-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Phone className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Emergency Dispatch
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Handle emergency maintenance requests with priority dispatch and real-time technician availability.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-purple-100 dark:bg-purple-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Settings className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Equipment Tracking
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Track facility equipment, warranties, and maintenance history for optimal asset management.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-red-100 dark:bg-red-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <DollarSign className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Cost Management
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Track maintenance costs, vendor expenses, and budget allocation across all facilities.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-orange-100 dark:bg-orange-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <BarChart3 className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Facility Analytics
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Track facility performance, maintenance trends, and identify areas for improvement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Common Facility Maintenance Use Cases
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              See how facility managers use WorkOrder HQ™ to streamline operations
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Use Case 1 */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8">
              <div className="flex items-center mb-4">
                <Building className="h-8 w-8 text-indigo-600 mr-3" />
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Office Buildings</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Maintain office buildings with HVAC, electrical, and plumbing systems for optimal tenant comfort.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  HVAC system maintenance
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Electrical system inspections
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Plumbing and restroom maintenance
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Elevator and security systems
                </li>
              </ul>
            </div>

            {/* Use Case 2 */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8">
              <div className="flex items-center mb-4">
                <Settings className="h-8 w-8 text-indigo-600 mr-3" />
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Retail Facilities</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Keep retail spaces operational with regular maintenance and rapid response to equipment failures.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Point-of-sale system maintenance
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Lighting and signage repairs
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  HVAC and climate control
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Security system maintenance
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Why Facility Managers Choose WorkOrder HQ™
              </h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Reduce Downtime
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Preventive maintenance reduces equipment failures and facility downtime by up to 40%.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Lower Maintenance Costs
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Proactive maintenance reduces emergency repairs and extends equipment life.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Improve Tenant Satisfaction
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Faster response times and better facility conditions keep tenants happy.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Ensure Compliance
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Track safety inspections and regulatory compliance requirements automatically.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-lg p-8 text-white">
              <div className="space-y-6">
                <div className="flex items-center">
                  <Clock className="h-8 w-8 mr-4" />
                  <div>
                    <p className="text-3xl font-bold">14 Days</p>
                    <p className="text-indigo-100">Free Trial</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Users className="h-8 w-8 mr-4" />
                  <div>
                    <p className="text-3xl font-bold">Up to 10</p>
                    <p className="text-indigo-100">Facility Managers</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <FileText className="h-8 w-8 mr-4" />
                  <div>
                    <p className="text-3xl font-bold">Unlimited</p>
                    <p className="text-indigo-100">Facilities</p>
                  </div>
                </div>
                <Link
                  to="/signup?industry=0"
                  className="block w-full bg-white text-indigo-600 text-center py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors mt-6"
                >
                  Get Started Free
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-indigo-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Facility Maintenance?
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Join facility managers who trust WorkOrder HQ™ to streamline their operations and improve facility performance.
          </p>
          <Link
            to="/signup?industry=0"
            className="inline-flex items-center px-8 py-4 text-lg font-semibold text-indigo-600 bg-white rounded-lg hover:bg-gray-100 transition-all transform hover:scale-105 shadow-lg"
          >
            Start Your Free Trial
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
          <p className="mt-4 text-sm text-indigo-100">
            No credit card required • Full access to all features
          </p>
        </div>
      </section>
    </div>
  );
};

export default FacilityMaintenanceIndustryPage;
